// ============================================================================
// LINKED FACET CARD COMPONENT
// Displays a facet that is linked to an entity with evidence and actions
// Used in entity detail panels to show supporting evidence
// ============================================================================

import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { FACET_TYPE_CONFIG, type FacetType, type FacetCategory } from '@/types/interviewer'
import {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link as LinkIcon,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
  FileText,
  ExternalLink,
  Unlink,
  Quote,
  type LucideIcon,
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

export interface ProblemStatement {
  context: string      // "When... [specific situation]"
  cause: string        // "This can cause... [negative impact]"
  consequence: string  // "This is problematic because... [impact]"
}

export interface LinkedFacet {
  uri: string
  facetType: string
  title?: string        // Short descriptive name (5-10 words)
  content: string       // Summary (1-2 sentences)
  evidence: string
  confidence: number
  problemStatement?: ProblemStatement  // Three C's for problem-type facets
  sourceUri?: string
  sourceTitle?: string
  status?: string
  createdAt?: string
}

interface LinkedFacetCardProps {
  facet: LinkedFacet
  onUnlink?: () => void
  onViewSource?: () => void
  isUnlinking?: boolean
  showActions?: boolean
  compact?: boolean
}

// ============================================================================
// ICON MAPPING
// ============================================================================

const ICON_MAP: Record<string, LucideIcon> = {
  AlertCircle,
  Activity,
  RefreshCw,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckSquare,
  GitBranch,
  Lightbulb,
  Lock,
  Users,
  Link: LinkIcon,
  HelpCircle,
  CheckCircle,
  TrendingUp,
  MessageCircle,
  Briefcase,
  Target,
  Sparkles,
  BarChart,
  FileText,
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getFacetStyle(facetTypeId: string): {
  category: FacetCategory
  color: string
  icon: LucideIcon
  label: string
} {
  const config = FACET_TYPE_CONFIG[facetTypeId as FacetType]

  if (config) {
    return {
      category: config.category,
      color: config.color,
      icon: ICON_MAP[config.icon] || FileText,
      label: config.label,
    }
  }

  // Fallback for unknown types
  const lowerType = facetTypeId.toLowerCase()

  if (lowerType.includes('customer') || lowerType.includes('problem') ||
      lowerType.includes('pain') || lowerType.includes('competitor')) {
    return { category: 'market', color: '#5c8a6b', icon: Target, label: facetTypeId }
  }

  if (lowerType.includes('decision') || lowerType.includes('goal') ||
      lowerType.includes('strategy') || lowerType.includes('revenue')) {
    return { category: 'business', color: '#4a90a4', icon: Briefcase, label: facetTypeId }
  }

  if (lowerType.includes('feature') || lowerType.includes('product') ||
      lowerType.includes('solution') || lowerType.includes('milestone')) {
    return { category: 'solution', color: '#c9a227', icon: Lightbulb, label: facetTypeId }
  }

  return { category: 'insights', color: '#6c757d', icon: MessageCircle, label: facetTypeId }
}

function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`
}

// ============================================================================
// LINKED FACET CARD COMPONENT
// ============================================================================

export function LinkedFacetCard({
  facet,
  onUnlink,
  onViewSource,
  isUnlinking = false,
  showActions = true,
  compact = false,
}: LinkedFacetCardProps) {
  const style = getFacetStyle(facet.facetType)
  const Icon = style.icon

  return (
    <Card
      className={cn(
        'p-3 transition-all border hover:bg-warm-50',
        compact ? 'p-2' : 'p-3'
      )}
      style={{ borderLeftColor: style.color, borderLeftWidth: '3px' }}
    >
      {/* Header Row */}
      <div className="flex items-start gap-2 mb-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${style.color}20` }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: style.color }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge
              variant="outline"
              className="text-xs"
              style={{ borderColor: style.color, color: style.color }}
            >
              {style.label}
            </Badge>

            {facet.confidence > 0 && (
              <span className="text-xs text-text-muted">
                {formatConfidence(facet.confidence)} confidence
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-1">
            {onViewSource && facet.sourceUri && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-text-muted hover:text-primary"
                onClick={onViewSource}
                title="View source"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </Button>
            )}
            {onUnlink && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-text-muted hover:text-solution"
                onClick={onUnlink}
                disabled={isUnlinking}
                title="Unlink from entity"
              >
                <Unlink className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Title & Content */}
      {facet.title && (
        <p className="text-sm font-medium text-text mb-1">
          {facet.title}
        </p>
      )}
      <p className={cn(
        'text-sm text-text-muted leading-relaxed',
        compact ? 'line-clamp-2' : 'line-clamp-3'
      )}>
        {facet.content}
      </p>

      {/* Problem Statement (Three C's) */}
      {facet.problemStatement && !compact && (
        <div className="mt-3 space-y-2 bg-warm-50 rounded-md p-3">
          <div className="flex items-start gap-2">
            <span className="text-xs font-semibold text-primary uppercase tracking-wide shrink-0 w-24">Context</span>
            <p className="text-xs text-text leading-relaxed">{facet.problemStatement.context}</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs font-semibold text-solution uppercase tracking-wide shrink-0 w-24">Cause</span>
            <p className="text-xs text-text leading-relaxed">{facet.problemStatement.cause}</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-xs font-semibold text-error uppercase tracking-wide shrink-0 w-24">Consequence</span>
            <p className="text-xs text-text leading-relaxed">{facet.problemStatement.consequence}</p>
          </div>
        </div>
      )}

      {/* Evidence Quote */}
      {facet.evidence && !compact && (
        <div className="mt-2 pl-3 border-l-2 border-warm-200">
          <div className="flex items-start gap-1">
            <Quote className="w-3 h-3 text-text-muted flex-shrink-0 mt-0.5" />
            <p className="text-xs text-text-muted italic line-clamp-2">
              {facet.evidence}
            </p>
          </div>
        </div>
      )}

      {/* Source Reference */}
      {facet.sourceTitle && (
        <div className="mt-2 flex items-center gap-1 text-xs text-text-muted">
          <FileText className="w-3 h-3" />
          <span className="truncate">{facet.sourceTitle}</span>
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// LINKED FACETS LIST COMPONENT
// For displaying multiple facets in entity detail views
// ============================================================================

interface LinkedFacetsListProps {
  facets: LinkedFacet[]
  onUnlink?: (facetUri: string) => void
  onViewSource?: (sourceUri: string) => void
  isUnlinking?: boolean
  emptyMessage?: string
  compact?: boolean
  maxVisible?: number
}

export function LinkedFacetsList({
  facets,
  onUnlink,
  onViewSource,
  isUnlinking = false,
  emptyMessage = 'No linked facets',
  compact = false,
  maxVisible,
}: LinkedFacetsListProps) {
  const visibleFacets = maxVisible ? facets.slice(0, maxVisible) : facets
  const hiddenCount = maxVisible ? Math.max(0, facets.length - maxVisible) : 0

  if (facets.length === 0) {
    return (
      <p className="text-sm text-text-muted italic text-center py-4">
        {emptyMessage}
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {visibleFacets.map((facet) => (
        <LinkedFacetCard
          key={facet.uri}
          facet={facet}
          onUnlink={onUnlink ? () => onUnlink(facet.uri) : undefined}
          onViewSource={onViewSource && facet.sourceUri
            ? () => onViewSource(facet.sourceUri!)
            : undefined
          }
          isUnlinking={isUnlinking}
          compact={compact}
        />
      ))}

      {hiddenCount > 0 && (
        <p className="text-xs text-text-muted text-center py-1">
          +{hiddenCount} more facet{hiddenCount > 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}

export default LinkedFacetCard
